import { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "static/styles/components/magicLinkSignin.css";
import { GoogleAuth } from "components/design/authWithGoogle";
import { sendMagicLink } from "apis/magicLinkApis";
import { SIGN_IN } from "constants/magicLink";
import { AuthUIWrapper } from "components/Auth/AuthUIWrapper";
import { SigninLayout } from "components/Auth/SigninLayout";
import Async from "components/DesignSystem/AsyncComponents/Async";
import {
  NEW_SIGNUP_FAILURE,
  NEW_SIGNUP_SUCCESS,
} from "constants/analyticsEvents";
import { FOREIGN_CA } from "constants/userTypes";
import { InvitationContext } from "contexts/InvitationContext";
import { useAnalytics } from "hooks/useAnalytics";
import { useLocalStorage } from "hooks/useLocalStorage";
import { useToast } from "hooks/useToast";
import { useTokenValidation } from "hooks/useTokenValidation";
import { useDispatch } from "react-redux";
import { useUpdateNameMutation } from "store/apis/settings";
import { setOnboardingStep } from "store/slices/onboarding";
import { postConversionToReditus } from "utils/analytics";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { SignUp } from "./signup/signUp";
import { SignIn } from "./signin/signIn";
import {
  ONBOARDING_CURRENT_STEP_KEY,
  useUserOnboardingStepper,
} from "hooks/useUserOnboardingStepper";
import { AuthButtonBar } from "components/Auth/AuthButtonBar";
import classNames from "classnames";
import { MCP_NEXT_URL } from "constants/storageKeys";
import { getAuthRedirectUrl } from "utils/getAuthRedirectUrl";

type MagicLinkProps = {
  recaptchaLoaded: boolean;
};

export type MagicLinkPayload = {
  first_name: string;
  last_name: string;
  email: string;
  uuid?: string;
};

type AuthorizeParams = {
  token: string;
  payload: MagicLinkPayload;
  origin: string;
};

type MagicLinkResponse = {
  data: {
    data: {
      mobile?: string;
      first_name: string;
      last_name: string;
      email: string;
    };
  };
};

declare const grecaptcha: {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

export const MagicLink = ({ recaptchaLoaded }: MagicLinkProps) => {
  const { alertToast } = useToast();
  const tokenValid = useTokenValidation();
  const { pathname, hash } = useLocation();
  const query = useQuery();
  const couponCode = query.get("couponCode") || query.get("couponcode");
  const isInvited = useContext(InvitationContext);
  const referralCode = query.get("ref");
  const { setItem } = useLocalStorage<string | null>("referralCode", null);
  const history = useHistory();
  const hashParams = new URLSearchParams(hash?.substring(1));
  const googleAccessToken = isInvited ? "" : hashParams.get("access_token");

  const [loading, setLoading] = useState(false);
  const { trackEvent } = useAnalytics();
  const [authLoading, setAuthLoading] = useState(false);
  const dispatch = useDispatch();
  const isPracticeUser = query.get("practice");
  const [updateName] = useUpdateNameMutation();
  const { update } = useUpdateQuery();
  const { stepInProgress, goNext, initialize } = useUserOnboardingStepper();
  const [askPhoneNumber, setAskPhoneNumber] = useState(true);
  const currentStep = localStorage.getItem(ONBOARDING_CURRENT_STEP_KEY);

  useEffect(() => {
    if (isInvited) {
      localStorage.removeItem(MCP_NEXT_URL);
      return;
    }

    if (pathname !== SIGN_IN) return;

    const urlParams = new URLSearchParams(window.location.search);
    const isMCPFlow = urlParams.get("mcp_state") === "1";
    const nextUrl = urlParams.get("next");

    if (isMCPFlow && nextUrl) {
      localStorage.setItem(MCP_NEXT_URL, nextUrl);
    }
  }, [pathname, isInvited]);

  useEffect(() => {
    if (isPracticeUser) {
      localStorage.setItem("user_type", FOREIGN_CA);
      update({ query: "practice", value: null });
    }
  }, [isPracticeUser, update]);

  useEffect(() => {
    if (couponCode) localStorage.setItem("couponCode", couponCode);
  }, [couponCode]);

  useEffect(() => {
    if (referralCode) setItem(referralCode);
  }, [referralCode, setItem]);

  useEffect(() => {
    if (!stepInProgress) {
      // Regular signup flow - start at SIGNUP step
      if (googleAccessToken) {
        setLoading(true);
      }
    }

    if (tokenValid && currentStep !== "SIGNUP") {
      history.replace("/");
    }
  }, [
    googleAccessToken,
    pathname,
    isInvited,
    tokenValid,
    history,
    dispatch,
    stepInProgress,
  ]);

  const authorize = async ({ token, payload, origin }: AuthorizeParams) => {
    const session_id = localStorage.getItem("TASK_SESSION_ID");
    const updatePayload = {
      ...payload,
      captcha_value: token,
      session_id,
      is_public_user: session_id ? true : false,
    };

    try {
      let response: MagicLinkResponse;

      if (isInvited) {
        const updateResponse = await updateName({
          payload: {
            first_name: payload.first_name,
            last_name: payload.last_name,
            profile_id: payload.uuid as string,
          },
        }).unwrap();

        response = {
          data: { data: updateResponse },
        };
      } else {
        response = await sendMagicLink(updatePayload);
      }

      if (!response.data.data.mobile) {
        trackEvent(NEW_SIGNUP_SUCCESS, {
          screen: "Sign Up",
          first_name: response.data.data.first_name,
          last_name: response.data.data.last_name,
          email: response.data.data.email,
        });
        postConversionToReditus({ email: response.data.data.email });
      }

      localStorage.setItem("magicLinkData", JSON.stringify(payload));

      initialize("PHONE_VERIFICATION");
      const couponCode = localStorage.getItem("couponCode");
      if (couponCode)
        history.push({
          pathname: isInvited ? "/invitation/phoneverify" : "otpverify",
          search: `?couponCode=${couponCode}&origin=${origin}`,
        });
      else
        history.push({
          pathname: isInvited ? "/invitation/phoneverify" : "otpverify",
          search: `?origin=${origin}`,
        });
      setAuthLoading(false);
      return true;
    } catch (e: any) {
      if (payload.first_name) {
        trackEvent(NEW_SIGNUP_FAILURE as any, {
          screen: "Sign Up",
          error_message: e?.response?.data?.error?.message,
          first_name: payload.first_name,
          last_name: payload.last_name,
          email: payload.email,
        });
      }
      alertToast({
        message: e?.response?.data?.error?.message,
        position: "bottom-left",
      });
      setAuthLoading(false);

      const redirectUrl = getAuthRedirectUrl(
        e?.response?.data?.error?.code,
        payload.email
      );

      if (redirectUrl) history.push(redirectUrl);

      return false;
    }
  };

  const updatePage = async (payload: MagicLinkPayload, origin: string) => {
    setAuthLoading(true);
    if (isInvited) {
      return authorize({ token: "", payload, origin });
    }

    grecaptcha.ready(function () {
      grecaptcha
        .execute(process.env.PUBLIC_RECAPTCHA_KEY as string, {
          action: "submit",
        })
        .then(async function (token) {
          authorize({ token, payload, origin });
        });
    });
  };

  const signinPath = pathname === SIGN_IN;
  const showSigninLayout = signinPath && !googleAccessToken;

  const handleSignin = (value: MagicLinkPayload) => updatePage(value, "signin");

  return (
    <Async.Root isSuccess={!loading} isLoading={loading} isEmpty={false}>
      <Async.Empty>
        <></>
      </Async.Empty>
      <Async.Success>
        {showSigninLayout ? (
          <SigninLayout>
            <SignIn updatePage={handleSignin} isLoading={authLoading} />
          </SigninLayout>
        ) : (
          <AuthUIWrapper
            isSignin={signinPath}
            className="t-overflow-auto"
            buttonBar={
              stepInProgress &&
              stepInProgress !== "SIGNUP" && (
                <AuthButtonBar backDisabled onNext={goNext} />
              )
            }
          >
            <div
              className={classNames("t-mt-[72px]", {
                "sm:t-mt-8": !signinPath,
                "sm:t-mt-0": signinPath,
              })}
            >
              {!googleAccessToken ? (
                <SignUp
                  updatePage={(value: MagicLinkPayload) =>
                    updatePage(value, "signup")
                  }
                  authLoading={authLoading || !recaptchaLoaded}
                />
              ) : (
                <GoogleAuth
                  googleAccessToken={googleAccessToken}
                  setLoading={setLoading}
                  onGoogleAuthFail={() => history.push(pathname)}
                  invitation={isInvited}
                  askPhoneNumber={askPhoneNumber}
                  setAskPhoneNumber={setAskPhoneNumber}
                />
              )}
            </div>
          </AuthUIWrapper>
        )}
      </Async.Success>
    </Async.Root>
  );
};
